<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  ACCESSIBILITY_UI_SETTINGS_KEY,
  createDefaultAccessibilityPreferences,
  normalizeAccessibilityPreferences,
} from './accessibilityPreferences';

const politeMessage = ref('');
const assertiveMessage = ref('');
const isSummarizing = ref(false);
const route = useRoute();
const { t } = useI18n();
const selectedChat = useMapGetter('getSelectedChat');
const { uiSettings } = useUISettings();

const preferences = ref(
  normalizeAccessibilityPreferences(
    uiSettings.value?.[ACCESSIBILITY_UI_SETTINGS_KEY] ||
      createDefaultAccessibilityPreferences()
  )
);

const isAssistiveModeEnabled = computed(
  () =>
    preferences.value.screenReaderMode || preferences.value.keyboardNavigation
);

const replaceLiveMessage = async (target, message) => {
  target.value = '';
  await nextTick();
  target.value = message;
};

const announce = (message, priority = 'polite') => {
  if (!message) return;
  if (priority === 'assertive') politeMessage.value = '';
  replaceLiveMessage(
    priority === 'assertive' ? assertiveMessage : politeMessage,
    message
  );
};

const getContactName = chat =>
  chat?.meta?.sender?.name ||
  chat?.meta?.sender?.available_name ||
  chat?.meta?.sender?.email ||
  t('ACCESSIBILITY.ANNOUNCER.CONTACT_WITHOUT_NAME');

const getConversationStatus = status => {
  const statuses = {
    open: t('ACCESSIBILITY.ANNOUNCER.STATUSES.OPEN'),
    pending: t('ACCESSIBILITY.ANNOUNCER.STATUSES.PENDING'),
    resolved: t('ACCESSIBILITY.ANNOUNCER.STATUSES.RESOLVED'),
    snoozed: t('ACCESSIBILITY.ANNOUNCER.STATUSES.SNOOZED'),
  };
  return (
    statuses[status] || status || t('ACCESSIBILITY.ANNOUNCER.UNKNOWN_STATUS')
  );
};

const getConversationBriefing = chat => {
  if (!chat?.id) return t('ACCESSIBILITY.ANNOUNCER.NO_CONVERSATION');

  const details = [
    t('ACCESSIBILITY.ANNOUNCER.BRIEFING', {
      id: chat.id,
      contact: getContactName(chat),
    }),
    `${t('ACCESSIBILITY.ANNOUNCER.STATUS')} ${getConversationStatus(chat.status)}`,
  ];

  if (chat.priority) {
    const priorities = {
      urgent: t('ACCESSIBILITY.ANNOUNCER.PRIORITIES.URGENT'),
      high: t('ACCESSIBILITY.ANNOUNCER.PRIORITIES.HIGH'),
      normal: t('ACCESSIBILITY.ANNOUNCER.PRIORITIES.NORMAL'),
      low: t('ACCESSIBILITY.ANNOUNCER.PRIORITIES.LOW'),
    };
    details.push(
      t('ACCESSIBILITY.ANNOUNCER.PRIORITY', {
        priority: priorities[chat.priority] || chat.priority,
      })
    );
  }
  if (chat.unread_count) {
    details.push(
      // Both keys are fixed translations selected from a numeric count.
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      t(
        chat.unread_count === 1
          ? 'ACCESSIBILITY.ANNOUNCER.UNREAD_ONE'
          : 'ACCESSIBILITY.ANNOUNCER.UNREAD_MANY',
        { count: chat.unread_count }
      )
    );
  }

  if (preferences.value.announcementVerbosity === 'detailed') {
    if (chat.meta?.team?.name) {
      details.push(
        t('ACCESSIBILITY.ANNOUNCER.TEAM', { name: chat.meta.team.name })
      );
    }
    if (chat.meta?.assignee?.name) {
      details.push(
        t('ACCESSIBILITY.ANNOUNCER.ASSIGNEE', { name: chat.meta.assignee.name })
      );
    }
    if (chat.sla_missed_events?.length) {
      details.push(t('ACCESSIBILITY.ANNOUNCER.SLA_BREACHED'));
    } else if (chat.applied_sla?.id) {
      details.push(t('ACCESSIBILITY.ANNOUNCER.SLA_ACTIVE'));
    }
  }

  return `${details.join('. ')}.`;
};

const focusRegion = (selector, regionName) => {
  const element = document.querySelector(selector);
  if (!element) {
    announce(
      t('ACCESSIBILITY.ANNOUNCER.REGION_UNAVAILABLE', { region: regionName })
    );
    return;
  }

  element.focus();
  announce(t('ACCESSIBILITY.ANNOUNCER.REGION_FOCUSED', { region: regionName }));
};

const requestConversationSummary = async () => {
  if (!selectedChat.value?.id) {
    announce(t('ACCESSIBILITY.ANNOUNCER.SUMMARY_OPEN_CONVERSATION_FIRST'));
    return;
  }
  if (isSummarizing.value) {
    announce(t('ACCESSIBILITY.ANNOUNCER.SUMMARY_IN_PROGRESS'));
    return;
  }

  try {
    isSummarizing.value = true;
    announce(t('ACCESSIBILITY.ANNOUNCER.SUMMARY_GENERATING'));
    const { data } = await window.axios.post(
      `/api/v1/accounts/${route.params.accountId}/lumerai/tasks/summarize`,
      { conversation_display_id: selectedChat.value.id }
    );
    announce(
      t('ACCESSIBILITY.ANNOUNCER.SUMMARY_RESULT', {
        summary: data.summary || data.message,
      }),
      'assertive'
    );
  } catch (error) {
    announce(
      error.response?.data?.error ||
        t('ACCESSIBILITY.ANNOUNCER.SUMMARY_FAILED'),
      'assertive'
    );
  } finally {
    isSummarizing.value = false;
  }
};

const describeNextAttachment = () => {
  const button = document.querySelector(
    '[data-a11y-describe-image], [data-a11y-describe-attachment]'
  );
  if (!button) {
    announce(t('ACCESSIBILITY.ANNOUNCER.NO_ATTACHMENT_TO_DESCRIBE'));
    return;
  }
  button.focus();
  button.click();
};

const handleGlobalKeydown = event => {
  if (!preferences.value.keyboardNavigation || event.defaultPrevented) return;
  if (!event.altKey || !event.shiftKey) return;

  const actions = {
    KeyC: () => focusRegion('#conversation-list-region', 'Lista de conversas'),
    KeyM: () =>
      focusRegion('#main-content', t('ACCESSIBILITY.ANNOUNCER.MAIN_CONTENT')),
    KeyR: () =>
      focusRegion(
        '#reply-composer [contenteditable="true"], #reply-composer textarea, .reply-box [contenteditable="true"]',
        'Campo de resposta'
      ),
    KeyL: () => announce(getConversationBriefing(selectedChat.value)),
    KeyS: requestConversationSummary,
    KeyI: describeNextAttachment,
  };

  const action = actions[event.code];
  if (!action) return;

  event.preventDefault();
  action();
};

const handleAnnouncement = event => {
  const { message, priority = 'polite' } = event.detail || {};
  announce(message, priority);
};

const handleAccessibilityCommand = event => {
  const action = event.detail?.action;
  const actions = {
    'focus-conversation-list': () =>
      focusRegion('#conversation-list-region', 'Lista de conversas'),
    'focus-main': () =>
      focusRegion('#main-content', t('ACCESSIBILITY.ANNOUNCER.MAIN_CONTENT')),
    'focus-reply': () =>
      focusRegion(
        '#reply-composer [contenteditable="true"], #reply-composer textarea, .reply-box [contenteditable="true"]',
        'Campo de resposta'
      ),
    briefing: () => announce(getConversationBriefing(selectedChat.value)),
    summary: requestConversationSummary,
    'describe-image': describeNextAttachment,
  };

  actions[action]?.();
};

const handlePreferencesChanged = event => {
  preferences.value = normalizeAccessibilityPreferences(event.detail);
};

watch(
  () => uiSettings.value?.[ACCESSIBILITY_UI_SETTINGS_KEY],
  value => {
    if (value) preferences.value = normalizeAccessibilityPreferences(value);
  },
  { deep: true }
);

watch(
  () => selectedChat.value?.id,
  async (conversationId, previousConversationId) => {
    if (
      !conversationId ||
      conversationId === previousConversationId ||
      !preferences.value.screenReaderMode ||
      !preferences.value.conversationBriefing
    ) {
      return;
    }

    await nextTick();
    announce(getConversationBriefing(selectedChat.value));
  }
);

watch(
  () => selectedChat.value?.messages?.length || 0,
  (messageCount, previousMessageCount) => {
    if (
      !preferences.value.screenReaderMode ||
      !selectedChat.value?.id ||
      messageCount <= previousMessageCount ||
      previousMessageCount === 0
    ) {
      return;
    }

    const lastMessage = selectedChat.value.messages.at(-1);
    if (lastMessage?.message_type === 0) {
      announce(
        t('ACCESSIBILITY.ANNOUNCER.NEW_MESSAGE', {
          contact: getContactName(selectedChat.value),
        })
      );
    }
  }
);

watch(
  () => selectedChat.value?.status,
  (status, previousStatus) => {
    if (
      !preferences.value.screenReaderMode ||
      !previousStatus ||
      status === previousStatus
    ) {
      return;
    }
    announce(
      t('ACCESSIBILITY.ANNOUNCER.CONVERSATION_STATUS_CHANGED', {
        status: getConversationStatus(status),
      })
    );
  }
);

watch(
  () => selectedChat.value?.sla_missed_events?.length || 0,
  (missedEvents, previousMissedEvents) => {
    if (
      preferences.value.screenReaderMode &&
      preferences.value.announceSla &&
      missedEvents > previousMissedEvents
    ) {
      announce(t('ACCESSIBILITY.ANNOUNCER.SLA_BREACHED_ALERT'), 'assertive');
    }
  }
);

watch(
  () => route.fullPath,
  async () => {
    if (!isAssistiveModeEnabled.value) return;
    await nextTick();
    const heading = document.querySelector(
      '#main-content h1, #main-content h2'
    );
    if (heading?.textContent?.trim()) {
      announce(
        t('ACCESSIBILITY.ANNOUNCER.PAGE_OPENED', {
          label: heading.textContent.trim(),
        })
      );
    }
  }
);

onMounted(() => {
  window.addEventListener('a11y-announce', handleAnnouncement);
  window.addEventListener('a11y-command', handleAccessibilityCommand);
  window.addEventListener('a11y-preferences-changed', handlePreferencesChanged);
  window.addEventListener('keydown', handleGlobalKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('a11y-announce', handleAnnouncement);
  window.removeEventListener('a11y-command', handleAccessibilityCommand);
  window.removeEventListener(
    'a11y-preferences-changed',
    handlePreferencesChanged
  );
  window.removeEventListener('keydown', handleGlobalKeydown);
});
</script>

<template>
  <div class="sr-only">
    <p role="status" aria-live="polite" aria-atomic="true">
      {{ politeMessage }}
    </p>
    <p role="alert" aria-live="assertive" aria-atomic="true">
      {{ assertiveMessage }}
    </p>
  </div>
</template>
